import { useEffect, useState } from "react";
import { JobPartCategory } from "../../models/Job";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import RsCoreImage from "../core/RsCoreImage";
import Modal from "../../layouts/Modal";
import { ArchiveBoxIcon, CheckCircleIcon, ClipboardDocumentListIcon, ExclamationTriangleIcon, PencilSquareIcon, PrinterIcon, QuestionMarkCircleIcon, ShieldExclamationIcon, TrashIcon } from "@heroicons/react/24/outline";
import useSinglePart from "../../data/useSinglePart";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useForm } from "react-hook-form";
import NotesOutput from "../shared/Notes/NotesOutput";
import { ResourceType } from "../../models/Note";
import { Assignee } from "../../models/Part";
import JobPartAspects from "./JobPartAspects";
import LoadingWheel from "../shared/LoadingWheel";


/** Shared grid for table header + data rows (min width enables horizontal scroll). */
export const JOB_PARTS_TABLE_ROW_GRID =
  "grid w-full min-w-[76rem] grid-cols-[2.25rem_minmax(12rem,1.2fr)_minmax(5.5rem,0.45fr)_minmax(5.75rem,0.5fr)_minmax(7rem,0.55fr)_minmax(6.5rem,0.45fr)_minmax(8.5rem,0.5fr)_minmax(16rem,1.1fr)_minmax(12rem,auto)] gap-x-0 gap-y-1 items-start px-0 text-sm [&>*]:border-r [&>*]:border-slate-100 [&>*:last-child]:border-r-0 [&>*]:px-2";

/** Compact grade toggles — letter only; full name in tooltip / criteria modal. */
const GRADE_BADGE_STYLES: Record<
  string,
  { selected: string; idle: string }
> = {
  A: {
    selected:
      "bg-emerald-600 text-white ring-emerald-700 shadow-sm",
    idle:
      "bg-white text-emerald-800 ring-emerald-300 hover:bg-emerald-50",
  },
  B: {
    selected: "bg-sky-600 text-white ring-sky-700 shadow-sm",
    idle: "bg-white text-sky-800 ring-sky-300 hover:bg-sky-50",
  },
  C: {
    selected:
      "bg-amber-500 text-white ring-amber-600 shadow-sm",
    idle:
      "bg-white text-amber-900 ring-amber-300 hover:bg-amber-50",
  },
  U: {
    selected: "bg-slate-600 text-white ring-slate-700 shadow-sm",
    idle: "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50",
  },
};

interface JobPartProps {
  partCategory: JobPartCategory;
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
  /** `row` = table-style band used inside `JobPartsByAssembly` table view. */
  variant?: "card" | "row";
}

export default function JobPart({
  partCategory,
  markAsRemoved,
  setDamaged,
  setDamagedOpen,
  handlePrint,
  selectedPartIds,
  togglePartSelection,
  onDiagramClick,
  variant = "card",
}: JobPartProps) {
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { patchPart, singlePart } = useSinglePart(partCategory.partDataId ?? "")
  const { register, watch, setValue: setFormValue } = useForm();

  const [open, setOpen] = useState(false);
  const [isSafetySelected, setIsSafetySelected] = useState(true);
  const [gradeInfoOpen, setGradeInfoOpen] = useState(false);
  const [storageOpen, setStorageOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const partData = singlePart.data?.data ?? partCategory.partData;
  const isWaitingForPart = !!partCategory.partDataId && singlePart.isLoading;

  useEffect(() => {
    if (isWaitingForPart) return;
    setFormValue("grade", partData?.grade ?? "");
  }, [isWaitingForPart, partData?.grade, setFormValue]);

  if (isWaitingForPart) {
    return (
      <div
        className={classNames(
          "flex items-center justify-center bg-white",
          variant === "row"
            ? "min-h-16 border-b border-slate-200 py-3"
            : "min-h-24 rounded-xl border-2 border-slate-300 p-4 shadow-md"
        )}
      >
        <LoadingWheel />
      </div>
    );
  }

  const partCategoryDefault = partData?.category?.partCategoryDefaults?.[0];
  const ebayCategoryId = partCategoryDefault?.ebayCategoryId ?? "";
  const dismantlerAspectNames = (partCategoryDefault?.aspectAssignees ?? [])
    .filter((aspectAssignee) => aspectAssignee.assignee === Assignee.Dismantler && aspectAssignee.aspectName)
    .map((aspectAssignee) => aspectAssignee.aspectName as string);

  const isSelected = partData?.id ? selectedPartIds.has(partData.id) : false;
  const isDeleted = !!(partData as any)?.deleted;
  const noteCount = partData?.internalNotes?.length ?? 0;

  const options = [
    { id: "A", name: "Grade A" },
    { id: "B", name: "Grade B" },
    { id: "C", name: "Grade C" },
    { id: "U", name: "Grade U" },
  ];

  const gradeValue = watch("grade") ?? "";

  const handleOpenNote = (isSafety: boolean) => {
    setIsSafetySelected(isSafety);
    setOpen(true);
  }

  const handleChangeMpn = (partNumber: string) => {

    if (partNumber == partData?.partNumber) return;

    if (!partNumber && !partData?.partNumber) return;

    const body: any = {
      partId: partCategory.partDataId,
      partNumber: partNumber
    };

    patchPart.mutate(body, saveHandlers);
  }

  const handleChangeGrade = (registerName: string, grade: string) => {
    setFormValue(registerName, grade);

    if (grade == partData?.grade) return;
    if (!grade && !partData?.grade) return;

    const body: any = {
      partId: partCategory.partDataId,
      grade
    };

    patchPart.mutate(body, saveHandlers);
  }

  const modalsEl = (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-4xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{isSafetySelected ? "Safety Information" : "Dismantling Instructions"}</h1>

          <hr />

          <p className="text-gray-700">{isSafetySelected ? partCategory.partCategory?.safetyNote : partCategory.partCategory?.dismantlingInstructions}</p>
        </div>
      </Modal>

      <Modal open={gradeInfoOpen} setOpen={setGradeInfoOpen} width="max-w-2xl">
        <div className="space-y-3">
          <h1 className="text-lg font-semibold text-gray-800">Grading criteria</h1>
          <p className="text-sm text-gray-600">Requirements for each grade when assessing part condition.</p>

          <div className="space-y-2 text-sm text-gray-700">
            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade A - Excellent</p>
              <p>Highest quality part. No, or minimal, damage, wear or corrosion. Fully functional, as new.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade B - Good</p>
              <p>Good condition. Small or moderate amount of damage or wear. Fully functional.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade C - Satisfactory</p>
              <p>Fair condition. Some wear or minor damage. Functional with possible cosmetic issues.</p>
            </div>

            <div className="rounded-sm bg-gray-100 px-3 py-2">
              <p className="font-semibold text-gray-800">Grade U - Not Tested/Out Of Scope</p>
              <p>Lowest quality part. Significant wear, damage or corrosion. May have functional issues.</p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={storageOpen} setOpen={setStorageOpen} width="max-w-2xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Storage Instructions</h1>
          <hr />
          <p className="text-gray-700">{partCategory.partCategory?.storageNote}</p>
        </div>
      </Modal>

      <Modal open={notesOpen} setOpen={setNotesOpen} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Notes</h1>
          <hr />
          <NotesOutput
            notes={partData?.internalNotes ?? []}
            resourceType={ResourceType.InternalPart}
            resourceId={partCategory.partDataId ?? ""}
            refetchQuery={["job"]}
            shouldScroll={false}
          />
        </div>
      </Modal>
    </>
  );

  if (variant === "row") {
    return (
      <>
        {modalsEl}
        <div className="group relative z-0 focus-within:z-40">
          <div
            className={classNames(
              JOB_PARTS_TABLE_ROW_GRID,
              "relative border-b border-slate-200 bg-white py-2 transition hover:bg-slate-50/90",
              "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-1",
              isDeleted
                ? "bg-rose-50/40 before:bg-rose-500"
                : partData?.isDamaged
                  ? "before:bg-rose-500"
                  : partData?.removedOn
                    ? "before:bg-emerald-500"
                    : "before:bg-slate-300",
              isSelected && "bg-primary-50/50 ring-1 ring-inset ring-primary-300"
            )}
          >
            <div className="flex items-start pt-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => togglePartSelection(partData?.id)}
                className="h-4 w-4 shrink-0 rounded border-slate-300 bg-white text-primary-600 focus:ring-primary-500"
                disabled={!partData?.id}
              />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1">
                <p className="line-clamp-2 text-sm font-semibold leading-tight text-slate-900">
                  {partData?.title}
                </p>
                {partData?.tagNumber && (
                  <span className="inline-flex shrink-0 items-center rounded bg-slate-100 px-1 py-0.5 font-mono text-[10px] font-medium text-slate-600">
                    #{partData.tagNumber}
                  </span>
                )}
              </div>
              {partCategory.partCategory?.name && (
                <p className="truncate text-[10px] text-slate-500">
                  {partCategory.partCategory.name}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              {isDeleted && (
                <span className="inline-flex w-fit items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                  <TrashIcon className="h-2.5 w-2.5" aria-hidden="true" />
                  Deleted
                </span>
              )}
              {partData?.removedOn && !isDeleted && (
                <span className="inline-flex w-fit items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                  <CheckCircleIcon className="h-2.5 w-2.5" aria-hidden="true" />
                  Removed
                </span>
              )}
              {partData?.isDamaged && (
                <span className="inline-flex w-fit items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                  <ExclamationTriangleIcon
                    className="h-2.5 w-2.5"
                    aria-hidden="true"
                  />
                  Damaged
                </span>
              )}
              {partData?.isCore && <RsCoreImage className="!h-4" />}
            </div>
            <div className="flex flex-wrap items-center content-center gap-0.5 self-center py-0.5">
              <input type="hidden" {...register("grade")} />
              {options.map((opt) => {
                const selected = gradeValue === opt.id;
                const tone =
                  GRADE_BADGE_STYLES[opt.id] ?? GRADE_BADGE_STYLES.U;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    title={opt.name}
                    aria-pressed={selected}
                    onClick={() => handleChangeGrade("grade", opt.id)}
                    className={classNames(
                      "min-h-[1.25rem] min-w-[1.375rem] rounded px-1 py-0.5 text-[10px] font-bold ring-1 ring-inset transition",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
                      selected ? tone.selected : tone.idle
                    )}
                  >
                    {opt.id}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setGradeInfoOpen(true)}
                className="rounded p-0.5 text-slate-400 hover:bg-slate-200/60 hover:text-slate-600"
                aria-label="Open grading criteria"
              >
                <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="self-center">
              <input
                onBlur={(e) => handleChangeMpn(e.target.value)}
                type="string"
                defaultValue={partData?.partNumber ?? ""}
                className="w-full min-w-0 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder="MPN"
              />
            </div>
            <div
              className="truncate self-center text-xs text-slate-700"
              title={partData?.oePartNumber ?? ""}
            >
              {partData?.oePartNumber ?? "—"}
            </div>
            <div className="flex flex-wrap content-start gap-1">
              {partCategory.partCategory?.safetyNote && (
                <button
                  type="button"
                  onClick={() => handleOpenNote(true)}
                  className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200 hover:bg-amber-100"
                >
                  <ShieldExclamationIcon
                    className="h-3 w-3"
                    aria-hidden="true"
                  />
                  Safe
                </button>
              )}
              {partCategory.partCategory?.dismantlingInstructions && (
                <button
                  type="button"
                  onClick={() => handleOpenNote(false)}
                  className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 ring-1 ring-inset ring-sky-200 hover:bg-sky-100"
                >
                  <ClipboardDocumentListIcon
                    className="h-3 w-3"
                    aria-hidden="true"
                  />
                  Inst
                </button>
              )}
              {partCategory.partCategory?.storageNote && (
                <button
                  type="button"
                  onClick={() => setStorageOpen(true)}
                  className="inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 ring-1 ring-inset ring-violet-200 hover:bg-violet-100"
                >
                  <ArchiveBoxIcon className="h-3 w-3" aria-hidden="true" />
                  Store
                </button>
              )}
              <button
                type="button"
                onClick={() => setNotesOpen(true)}
                className="inline-flex items-center gap-0.5 rounded-full bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
              >
                <PencilSquareIcon className="h-3 w-3" aria-hidden="true" />
                Notes{noteCount > 0 ? ` ${noteCount}` : ""}
              </button>
            </div>
            <div className="min-w-0">
              {partData?.id &&
              ebayCategoryId &&
              dismantlerAspectNames.length > 0 ? (
                <JobPartAspects
                  partDataId={partData.id}
                  ebayCategoryId={ebayCategoryId}
                  dismantlerAspectNames={dismantlerAspectNames}
                  existingAspects={partData.aspects ?? []}
                  onSaved={() => singlePart.refetch()}
                  compact
                />
              ) : (
                <span className="text-[10px] text-slate-400">—</span>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              {!partData?.isDamaged && (
                <>
                  <ButtonInput
                    label={partData?.removedOn ? "Removed" : "Remove"}
                    isSubmit={false}
                    onClick={() => markAsRemoved(partData!)}
                    classes={classNames(
                      "!py-0.5 !px-1.5 !text-[10px]",
                      partData?.removedOn
                        ? "!bg-emerald-100 !text-emerald-700 hover:!bg-emerald-100"
                        : "!bg-emerald-600 hover:!bg-emerald-700"
                    )}
                    disabled={partData?.removedOn !== null}
                  />
                  <ButtonInput
                    label="Damaged"
                    isSubmit={false}
                    onClick={() => {
                      setDamaged(partData);
                      setDamagedOpen(true);
                    }}
                    classes="!py-0.5 !px-1.5 !text-[10px] !bg-white !text-rose-700 !ring-1 !ring-inset !ring-rose-200 hover:!bg-rose-50"
                  />
                </>
              )}
              {onDiagramClick && partCategory.partCategory && (
                <ButtonInput
                  label={
                    partCategory.partCategory.ucidZone && partData?.matchedUcid
                      ? "Diagram"
                      : "Find"
                  }
                  isSubmit={false}
                  onClick={() => onDiagramClick(partCategory)}
                  classes={classNames(
                    "!py-0.5 !px-1.5 !text-[10px]",
                    partCategory.partCategory.ucidZone && partData?.matchedUcid
                      ? "!bg-primary-50 !text-primary-700 !ring-1 !ring-inset !ring-primary-200 hover:!bg-primary-100"
                      : "!bg-white !text-slate-600 !ring-1 !ring-inset !ring-slate-200 hover:!bg-slate-50"
                  )}
                />
              )}
              <ButtonInput
                label="Stock"
                isSubmit={false}
                onClick={() => openInNewTab(`/stock/${partData?.id}`)}
                classes="!py-0.5 !px-1.5 !text-[10px] !bg-white !text-slate-700 !ring-1 !ring-inset !ring-slate-200 hover:!bg-slate-50"
              />
              <button
                type="button"
                onClick={() => handlePrint(partData!)}
                title="Print label"
                aria-label="Print label"
                className="inline-flex items-center justify-center rounded border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50"
              >
                <PrinterIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {modalsEl}
      <div
        key={partCategory.partDataId}
        className={classNames(
          // Tile shell — no padding on root so the footer can be a full-width
          // action band. Same overflow/z-index rules as before for dropdowns.
          "group relative z-0 flex flex-col overflow-visible rounded-xl border-2 bg-white shadow-md ring-1 ring-slate-200/60 transition hover:border-slate-400 hover:shadow-lg focus-within:z-40",
          "before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[10px]",
          isDeleted
            ? "border-rose-300 bg-rose-50/50 before:bg-rose-500"
            : partData?.isDamaged
              ? "border-rose-300 before:bg-rose-500"
              : partData?.removedOn
                ? "border-emerald-300 before:bg-emerald-500"
                : "border-slate-300 before:bg-slate-300",
          isSelected && "ring-2 ring-primary-500 ring-offset-1"
        )}
      >
        <div className="space-y-3 p-4 pb-3">
        {/* Header — title band separated from the rest for quicker scanning. */}
        <div className="flex items-start gap-2.5 border-b border-slate-100 pb-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => togglePartSelection(partData?.id)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 bg-white text-primary-600 focus:ring-primary-500"
            disabled={!partData?.id}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="text-base font-semibold leading-snug text-slate-900">
                {partData?.title}
              </p>
              {partData?.tagNumber && (
                <span className="inline-flex items-center rounded-md border border-slate-200/80 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-600">
                  #{partData.tagNumber}
                </span>
              )}
            </div>
            {partCategory.partCategory?.name && (
              <p className="mt-1 truncate text-xs font-medium text-slate-500">
                {partCategory.partCategory.name}
              </p>
            )}
          </div>

          <div className="flex max-w-[40%] shrink-0 flex-wrap items-center justify-end gap-1">
            {isDeleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                <TrashIcon className="h-3 w-3" aria-hidden="true" />
                Deleted
              </span>
            )}
            {partData?.removedOn && !isDeleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                <CheckCircleIcon className="h-3 w-3" aria-hidden="true" />
                Removed
              </span>
            )}
            {partData?.isDamaged && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
                <ExclamationTriangleIcon
                  className="h-3 w-3"
                  aria-hidden="true"
                />
                Damaged
              </span>
            )}
            {partData?.isCore && <RsCoreImage className="!h-5" />}
          </div>
        </div>

        {/* Quick links — subtle toolbar so chips don’t compete with the title. */}
        <div className="flex flex-wrap gap-1.5 rounded-lg bg-slate-50/90 px-2 py-1.5 ring-1 ring-inset ring-slate-100">
            {partCategory.partCategory?.safetyNote && (
              <button
                type="button"
                onClick={() => handleOpenNote(true)}
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200 hover:bg-amber-100"
              >
                <ShieldExclamationIcon
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
                Safety
              </button>
            )}
            {partCategory.partCategory?.dismantlingInstructions && (
              <button
                type="button"
                onClick={() => handleOpenNote(false)}
                className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200 hover:bg-sky-100"
              >
                <ClipboardDocumentListIcon
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />
                Instructions
              </button>
            )}
            {partCategory.partCategory?.storageNote && (
              <button
                type="button"
                onClick={() => setStorageOpen(true)}
                className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-200 hover:bg-violet-100"
              >
                <ArchiveBoxIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Storage
              </button>
            )}
            <button
              type="button"
              onClick={() => setNotesOpen(true)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Notes{noteCount > 0 ? ` (${noteCount})` : ""}
            </button>
        </div>

        {/* Identification — MPN + OE on one row; grade on its own row so it
            reads as a deliberate step after part numbers. */}
        <div className="rounded-lg border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/40 p-3 shadow-sm ring-1 ring-slate-100/60">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Identification
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                MPN
              </label>
              <input
                onBlur={(e) => handleChangeMpn(e.target.value)}
                type="string"
                defaultValue={partData?.partNumber ?? ""}
                className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder="Add MPN"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                OE MPN
              </label>
              <p className="mt-0.5 truncate rounded-md border border-slate-200/80 bg-white px-2 py-1.5 text-sm text-slate-700 shadow-sm">
                {partData?.oePartNumber ?? (
                  <span className="text-slate-400">—</span>
                )}
              </p>
            </div>
          </div>
          <div className="mt-3 border-t border-slate-200/80 pt-3">
            <input type="hidden" {...register("grade")} />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Grade
              </span>
              <div
                className="inline-flex flex-wrap gap-1"
                role="group"
                aria-label="Part grade"
              >
                {options.map((opt) => {
                  const selected = gradeValue === opt.id;
                  const tone =
                    GRADE_BADGE_STYLES[opt.id] ?? GRADE_BADGE_STYLES.U;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      title={opt.name}
                      aria-pressed={selected}
                      onClick={() => handleChangeGrade("grade", opt.id)}
                      className={classNames(
                        "min-h-[1.375rem] min-w-[1.625rem] rounded-md px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset transition",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
                        selected ? tone.selected : tone.idle
                      )}
                    >
                      {opt.id}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setGradeInfoOpen(true)}
                className="shrink-0 rounded-md p-0.5 text-slate-400 hover:bg-slate-200/60 hover:text-slate-600"
                aria-label="Open grading criteria"
              >
                <QuestionMarkCircleIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {partData?.id && ebayCategoryId && dismantlerAspectNames.length > 0 && (
          <JobPartAspects
            partDataId={partData.id}
            ebayCategoryId={ebayCategoryId}
            dismantlerAspectNames={dismantlerAspectNames}
            existingAspects={partData.aspects ?? []}
            onSaved={() => singlePart.refetch()}
            embedded
          />
        )}
        </div>

        {/* Action band — fixed to the foot of the tile so primary buttons
            always sit in the same place across cards. */}
        <div className="mt-auto flex flex-wrap items-center gap-2 rounded-b-[10px] border-t border-slate-200 bg-slate-50/90 px-4 py-3">
          {!partData?.isDamaged && (
            <>
              <ButtonInput
                label={partData?.removedOn ? "Removed" : "Mark Removed"}
                isSubmit={false}
                onClick={() => markAsRemoved(partData!)}
                classes={classNames(
                  "!py-1 !px-2 !text-xs",
                  partData?.removedOn
                    ? "!bg-emerald-100 !text-emerald-700 hover:!bg-emerald-100"
                    : "!bg-emerald-600 hover:!bg-emerald-700"
                )}
                disabled={partData?.removedOn !== null}
              />
              <ButtonInput
                label={"Mark Damaged"}
                isSubmit={false}
                onClick={() => {
                  setDamaged(partData);
                  setDamagedOpen(true);
                }}
                classes={
                  "!py-1 !px-2 !text-xs !bg-white !text-rose-700 !ring-1 !ring-inset !ring-rose-200 hover:!bg-rose-50"
                }
              />
            </>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {onDiagramClick &&
              partCategory.partCategory &&
              (() => {
                const canView =
                  partCategory.partCategory!.ucidZone && partData?.matchedUcid;
                return (
                  <ButtonInput
                    label={canView ? "Diagram" : "Find Diagram"}
                    isSubmit={false}
                    onClick={() => onDiagramClick(partCategory)}
                    classes={classNames(
                      "!py-1 !px-2 !text-xs",
                      canView
                        ? "!bg-primary-50 !text-primary-700 !ring-1 !ring-inset !ring-primary-200 hover:!bg-primary-100"
                        : "!bg-white !text-slate-600 !ring-1 !ring-inset !ring-slate-200 hover:!bg-slate-50"
                    )}
                  />
                );
              })()}
            <ButtonInput
              label={"View Stock"}
              isSubmit={false}
              onClick={() => openInNewTab(`/stock/${partData?.id}`)}
              classes={
                "!py-1 !px-2 !text-xs !bg-white !text-slate-700 !ring-1 !ring-inset !ring-slate-200 hover:!bg-slate-50"
              }
            />
            <button
              type="button"
              onClick={() => handlePrint(partData!)}
              title="Print Label"
              aria-label="Print Label"
              className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white p-1.5 text-slate-600 hover:bg-slate-50"
            >
              <PrinterIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
